// Bakiye Tutarlılık Kontrolü — admin sayfası.
// Cari / Kasa / Banka kart bakiyelerini hareketler tablosuyla karşılaştırır.
// Drift varsa raporlar, "Onar" butonu ile yeniden hesaplar.

import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button, EmptyState, ListPageHeader } from '../components/ui';
import { fmtAmount } from '../utils/numberFormat';

export default function BalanceCheckPage() {
  const [data, setData] = useState({ accounts: [], cash: [], bank: [], expenseCards: [], stock: [], summary: { total_drift_count: 0 } });
  const [summary, setSummary] = useState(null);          // health-summary card data
  const [history, setHistory] = useState([]);            // 30 gün snapshot serisi
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchDrift = useCallback(async () => {
    setLoading(true);
    try {
      const [drift, sum, hist] = await Promise.all([
        api.get('/balance-check'),
        api.get('/balance-check/health-summary').catch(() => null),
        api.get('/balance-check/health-history?days=30').catch(() => null),
      ]);
      if (drift.data.success) setData(drift.data.data);
      if (sum?.data?.success) setSummary(sum.data.data);
      if (hist?.data?.success) setHistory((hist.data.data || []).slice().reverse());
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Kontrol başarısız' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDrift(); }, [fetchDrift]);

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4000); };

  const recompute = async (kind, id, name) => {
    if (!window.confirm(`"${name}" için bakiye yeniden hesaplanacak. Devam edilsin mi?`)) return;
    setWorking(true);
    try {
      await api.post('/balance-check/recompute/' + kind + '/' + id);
      showMsg('success', `${name} bakiyesi onarıldı`);
      fetchDrift();
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Onarım başarısız');
    } finally { setWorking(false); }
  };

  const recomputeAll = async () => {
    if (!window.confirm('TÜM cari/kasa/banka/harcama-kartı/stok bakiyeleri yeniden hesaplanacak. Bu işlem birkaç saniye sürebilir. Devam edilsin mi?')) return;
    setWorking(true);
    try {
      const r = await api.post('/balance-check/recompute-all');
      if (r.data.success) {
        const d = r.data.data;
        showMsg('success', `Onarım tamamlandı: ${d.total} kayıt (cari: ${d.accounts}, kasa: ${d.cash}, banka: ${d.bank}, harcama kartı: ${d.expense_cards}, stok: ${d.stock})`);
        fetchDrift();
      }
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Toplu onarım başarısız');
    } finally { setWorking(false); }
  };

  const Section = ({ title, icon, kind, items, nameField, unit = '₺', extraField = null, extraLabel = null }) => (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border-secondary)',
      borderRadius: 12, padding: 16, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 22 }}>{icon}</span>
        <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>{title}</h3>
        <span style={{
          marginLeft: 'auto', padding: '3px 12px', borderRadius: 'var(--r-full)',
          background: items.length > 0 ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.15)',
          color: items.length > 0 ? '#f59e0b' : '#10b981',
          fontSize: 12, fontWeight: 700,
        }}>
          {items.length > 0 ? `${items.length} tutarsızlık` : '✓ Tutarlı'}
        </span>
      </div>

      {items.length === 0 ? (
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
          ✓ Bu modülde tutarsızlık yok — kart bakiyesi ve hareketler toplamı eşit
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)', textAlign: 'left' }}>
                <th style={th}>Kayıt</th>
                <th style={{ ...th, textAlign: 'right' }}>Kart Bakiyesi</th>
                <th style={{ ...th, textAlign: 'right' }}>Hareketler Toplamı</th>
                <th style={{ ...th, textAlign: 'right' }}>Fark</th>
                <th style={{ ...th, textAlign: 'center', width: 120 }}>Aksiyon</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => {
                const name = it[nameField] || it.title || it.code;
                const diffAbs = Math.abs(it.diff);
                return (
                  <tr key={it.id} style={{ borderTop: '1px solid var(--border-secondary)' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{name}</div>
                      {it.code && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{it.code}</div>}
                      {extraField && it[extraField] && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{extraLabel}: {it[extraField]}</div>}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmount(it.cached)} {unit}</td>
                    <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmount(it.computed)} {unit}</td>
                    <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: 'var(--danger)' }}>
                      {it.diff > 0 ? '+' : ''}{fmtAmount(it.diff)} {unit}
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <Button size="sm" variant="danger" disabled={working}
                        onClick={() => recompute(kind, it.id, name)}>
                        🔧 Onar
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const total = data.summary?.total_drift_count || 0;

  return (
    <div className="stocks-page" style={{ padding: 20 }}>
      <ListPageHeader
        title="Sistem Sağlığı — Bakiye Doğrulama"
        subtitle={total > 0 ? `${total} kayıt için yeniden hesaplama gerekiyor` : '✓ Tüm modüller senkron'}
        onRefresh={fetchDrift}
        refreshing={loading}
        actions={
          total > 0 && (
            <Button variant="danger" onClick={recomputeAll} disabled={working}>
              🔧 Senkronize Et
            </Button>
          )
        }
      />

      {msg && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, margin: '10px 0',
          background: msg.type === 'success' ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)',
          border: '1px solid ' + (msg.type === 'success' ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)'),
          color: msg.type === 'success' ? '#10b981' : '#dc2626',
          fontSize: 13, fontWeight: 600,
        }}>
          {msg.text}
        </div>
      )}

      <div style={{
        padding: 14, borderRadius: 10, marginBottom: 16,
        background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.25)',
        fontSize: 13, color: 'var(--text-secondary)',
      }}>
        🔒 <strong>Vendor (HENT) personeli için sistem aracı.</strong> Müşteri tenant'larında bakiye verilerinin senkron olduğunu doğrular. Worker 4 saatte bir tüm tenant'ları tarar; senkronizasyon gerekirse buradan tek tık ile uygulanır. Müşteri tarafı tutarsızlığı görmez.
      </div>

      {summary && <HealthCards summary={summary} totalDrift={total} />}
      {history.length > 0 && <HealthHistoryChart history={history} />}

      <Section title="Cari Hesaplar"   icon="👥" kind="account"      items={data.accounts}     nameField="title" />
      <Section title="Kasalar"          icon="💵" kind="cash"         items={data.cash}         nameField="name" />
      <Section title="Banka Hesapları"  icon="🏦" kind="bank"         items={data.bank}         nameField="name" />
      <Section title="Harcama Kartları" icon="💳" kind="expense_card" items={data.expenseCards || []} nameField="name" />
      <Section title="Stok"             icon="📦" kind="stock"        items={data.stock || []}        nameField="name" unit="ad" extraField="warehouse" extraLabel="Depo" />
    </div>
  );
}

const th = { padding: '8px 10px', fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4 };
const td = { padding: '10px' };

// ─── Sağlık özet kartları + 30 günlük mini grafik ──────────────────
function HealthCards({ summary, totalDrift }) {
  const trgOk = summary.triggers_present === summary.triggers_expected && summary.triggers_disabled === 0;
  const lastCheck = summary.last_check_at ? new Date(summary.last_check_at) : null;
  const lastCheckText = lastCheck
    ? `${Math.round((Date.now() - lastCheck.getTime()) / 60000)} dk önce`
    : '— (worker henüz koşmadı)';
  const cards = [
    { label: 'DB Boyutu', value: summary.db_size_mb != null ? `${summary.db_size_mb} MB` : '—', color: '#3b82f6', icon: '💾' },
    { label: 'Aktif Drift', value: totalDrift, color: totalDrift > 0 ? '#f59e0b' : '#10b981', icon: totalDrift > 0 ? '⚠' : '✓' },
    { label: 'Trigger Durumu', value: `${summary.triggers_present}/${summary.triggers_expected}`, color: trgOk ? '#10b981' : '#dc2626', icon: trgOk ? '🛡' : '🚨' },
    { label: 'Son Kontrol', value: lastCheckText, color: '#7c3aed', icon: '⏱' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
      {cards.map(c => (
        <div key={c.label} style={{
          background: 'var(--bg-card)', border: '1px solid var(--border-secondary)',
          borderRadius: 10, padding: '12px 14px',
          borderLeft: `4px solid ${c.color}`,
        }}>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
            {c.icon} {c.label}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: c.color }}>{c.value}</div>
        </div>
      ))}
      {(summary.missing_triggers?.length > 0 || summary.disabled_triggers?.length > 0) && (
        <div style={{
          gridColumn: '1 / -1',
          background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)',
          borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#dc2626',
        }}>
          🚨 <strong>KRİTİK:</strong> {summary.missing_triggers?.length > 0 && `Eksik trigger: ${summary.missing_triggers.join(', ')}`} {summary.disabled_triggers?.length > 0 && `Disable trigger: ${summary.disabled_triggers.join(', ')}`}
        </div>
      )}
    </div>
  );
}

function HealthHistoryChart({ history }) {
  // İki seri: DB boyut (mavi alan) + drift sayısı (sarı bar overlay)
  const W = 800, H = 140, PAD = 30;
  const xs = history.map((_, i) => PAD + (i * (W - 2 * PAD)) / Math.max(history.length - 1, 1));
  const maxMb = Math.max(...history.map(h => h.db_size_mb || 0), 1);
  const maxDrift = Math.max(...history.map(h => h.total_drift_count || 0), 1);
  const dbY = (mb) => H - PAD - ((mb || 0) / maxMb) * (H - 2 * PAD);
  const path = history.map((h, i) => `${i === 0 ? 'M' : 'L'}${xs[i]},${dbY(h.db_size_mb)}`).join(' ');
  const areaPath = path + ` L${xs[xs.length - 1]},${H - PAD} L${xs[0]},${H - PAD} Z`;
  const firstDate = new Date(history[0]?.checked_at).toLocaleDateString('tr-TR');
  const lastDate = new Date(history[history.length - 1]?.checked_at).toLocaleDateString('tr-TR');
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border-secondary)',
      borderRadius: 10, padding: 14, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <strong style={{ fontSize: 13 }}>📈 Son 30 Gün Sağlık Snapshot'ları ({history.length} kayıt)</strong>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{firstDate} → {lastDate}</span>
      </div>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
        {/* Grid */}
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="var(--border-secondary)" />
        {/* DB boyut alanı */}
        <path d={areaPath} fill="rgba(59,130,246,0.15)" />
        <path d={path} stroke="#3b82f6" strokeWidth="2" fill="none" />
        {/* Drift bar'ları */}
        {history.map((h, i) => {
          if (!h.total_drift_count) return null;
          const barH = (h.total_drift_count / maxDrift) * (H - 2 * PAD) * 0.6;
          return (
            <rect key={i} x={xs[i] - 2} y={H - PAD - barH} width={4} height={barH} fill="#f59e0b" opacity={0.8} />
          );
        })}
        {/* Eksen yazıları */}
        <text x={PAD} y={H - 8} fontSize="10" fill="var(--text-tertiary)">{firstDate}</text>
        <text x={W - PAD - 50} y={H - 8} fontSize="10" fill="var(--text-tertiary)">{lastDate}</text>
        <text x={PAD - 25} y={dbY(maxMb) + 4} fontSize="10" fill="#3b82f6">{maxMb} MB</text>
      </svg>
      <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6 }}>
        <span><span style={{ display: 'inline-block', width: 12, height: 4, background: '#3b82f6', verticalAlign: 'middle', marginRight: 4 }} />DB Boyutu (MB)</span>
        <span><span style={{ display: 'inline-block', width: 4, height: 10, background: '#f59e0b', verticalAlign: 'middle', marginRight: 4 }} />Drift sayısı</span>
      </div>
    </div>
  );
}
